/**
 * Determines whether two objects of the same type are different.
 *
 * - Checks for strict inequality
 * - Then for nullish values
 * - Then iterates through the properties of the first object, checking if both objects have the same property and value.
 *
 * Note: This function does a shallow comparison, so nested objects or arrays will be compared by reference, not by value.
 * The order of the proporties are not considered.
 *
 * @param {T} obj1 - The first object to compare.
 * @param {T} obj2 - The second object to compare, of the same type as the first.
 * @returns {boolean} - Returns `true` if the objects are different, otherwise `false`.
 * @template T - The type of the objects being compared.
 */

export function areObjectsDifferent<T>(obj1: T, obj2: T): boolean {
  if (obj1 === obj2) {
    return false;
  }

  if (
    obj1 === null ||
    obj2 === null ||
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object'
  ) {
    return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return true;
  }

  for (const key in obj1) {
    if (!keys2.includes(key) || obj1[key] !== obj2[key]) {
      return true;
    }
  }

  return false;
}
